import { FormControl, FormGroup } from '@angular/forms';
import { ElementValidationResult, LevelDesignerElementBase } from './element-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { PropertyMetadata } from '../properties/property-types';

export abstract class PointElement extends LevelDesignerElementBase {
    public x: number;
    public y: number;
    protected abstract sizeInMeters: number;
    protected color: string = '#000000';
    private svgImage: HTMLImageElement | null = null;
    protected canvas: HTMLCanvasElement | null = null;
    private levelDesigner: LevelDesignerComponent | null = null;

    constructor(formGroup: FormGroup, private svgPath: string) {
        super(formGroup);
        
        if (!formGroup.contains('LocationPoints')) {
            formGroup.addControl('LocationPoints', new FormControl([0.001, 0.001]));
        }
        const initialLocation : number[] | null = formGroup.get('LocationPoints')?.value;
        if (initialLocation == null || initialLocation.length === 0) {
            formGroup.get('LocationPoints')?.setValue([0.001, 0.001]);
        }
        this.x = formGroup.get('LocationPoints')?.value[0] * 1000;
        this.y = formGroup.get('LocationPoints')?.value[1] * 1000;
        // Convert degrees to radians when loading from form
        const angleDegrees = formGroup.get('DisplayAngle')?.value || 0;
        this.DisplayAngle = angleDegrees;
        this.loadSvgImage();

        
    }

    public setLevelDesigner(designer: LevelDesignerComponent) {
        this.levelDesigner = designer;
    }

    private loadSvgImage() {
        this.svgImage = new Image();
        this.svgImage.onerror = (e) => {
            console.error('Failed to load SVG:', this.svgPath, e);
        };
        this.svgImage.onload = () => {
            if (this.levelDesigner) {
                this.levelDesigner.redraw();
            }
        };
        this.svgImage.src = this.svgPath;
    }

    override draw(ctx: CanvasRenderingContext2D, offsetX?: number, offsetY?: number): void {
        // Store canvas reference for later use
        this.canvas = ctx.canvas;
        
        ctx.save();
        ctx.translate(this.x + (offsetX??0), this.y + (offsetY??0));
        
        // Draw the SVG image if loaded
        if (this.svgImage && this.svgImage.complete) {
            const size = this.sizeInMeters; // Size in world units (meters)
            try {
                const angle = this.DisplayAngle * Math.PI / 180;
                ctx.rotate(angle);
                ctx.drawImage(
                    this.svgImage,
                    -size/2, -size/2,  // Center the image on the point
                    size, size         // Draw at specified size
                );
                ctx.rotate(-angle); // Reset rotation
            } catch (error) {
                console.error('Error drawing SVG image:', error);
            }
        }

        if (this.selected) {
            // Draw selection box
            const halfSize = this.sizeInMeters/2;
            ctx.beginPath();
            ctx.rect(-halfSize, -halfSize, this.sizeInMeters, this.sizeInMeters);
            ctx.strokeStyle = LevelDesignerElementBase.selectionColor;
            ctx.setLineDash([0.1, 0.1]);
            ctx.lineWidth = 0.04;
            ctx.stroke();

            // Draw vertex indicators at corners
            ctx.fillStyle = 'blue';
            const corners = [
                [-halfSize, -halfSize],
                [halfSize, -halfSize],
                [halfSize, halfSize],
                [-halfSize, halfSize]
            ];
            
            corners.forEach(corner => {
                ctx.beginPath();
                ctx.arc(corner[0], corner[1], 0.1 , 0, Math.PI * 2);
                ctx.fill();
            });
        }

        ctx.restore();
    }

    public drawGeo(ctx: CanvasRenderingContext2D): void {
        ctx.save();
        
        // Set styling based on element type
        let fillColor = 'rgba(255, 165, 0, 0.6)'; // Default orange
        let strokeColor = 'rgba(255, 165, 0, 0.9)';
        let size = this.sizeInMeters;
        
        // Customize appearance based on element type
        if (this.TypeName.includes('Light')) {
            fillColor = 'rgba(255, 255, 0, 0.6)'; // Yellow for lights
            strokeColor = 'rgba(255, 255, 0, 0.9)';
        } else if (this.TypeName.includes('Camera')) {
            fillColor = 'rgba(0, 128, 255, 0.6)'; // Blue for cameras
            strokeColor = 'rgba(0, 128, 255, 0.9)';
        } else if (this.TypeName.includes('Barrier')) {
            fillColor = 'rgba(255, 0, 0, 0.6)'; // Red for barriers
            strokeColor = 'rgba(255, 0, 0, 0.9)';
        } else if (this.TypeName.includes('Controller')) {
            fillColor = 'rgba(128, 0, 128, 0.6)'; // Purple for controllers
            strokeColor = 'rgba(128, 0, 128, 0.9)';
        }
        
        // Draw a filled circle with border
        ctx.beginPath();
        ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
        ctx.fillStyle = fillColor;
        ctx.fill();
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        
        // Draw a direction indicator if the element has an angle
        if (this.DisplayAngle !== 0) {
            const angleRad = this.DisplayAngle * Math.PI / 180;
            ctx.beginPath();
            ctx.moveTo(this.x, this.y);
            ctx.lineTo(
                this.x + Math.cos(angleRad) * size * 1.5,
                this.y + Math.sin(angleRad) * size * 1.5
            );
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 0.5;
            ctx.stroke();
        }
        
        ctx.restore();
    }

    isPointInside(x: number, y: number): boolean {
        const dx = x - this.x;
        const dy = y - this.y;
        return dx * dx + dy * dy <= this.sizeInMeters * this.sizeInMeters;
    }

    getBoundingBox(): { x: number, y: number, width: number, height: number } {
        return {
            x: this.x - this.sizeInMeters,
            y: this.y - this.sizeInMeters,
            width: this.sizeInMeters * 2,
            height: this.sizeInMeters * 2
        };
    }

    move(deltaX: number, deltaY: number): void {
        this.x += deltaX;
        this.y += deltaY;
    }

    // clone(): LevelDesignerElementBase {
    //     return null; //new PointElement(this.x, this.y, this.radius, this.color);
    // }

    toJSON(): object {
        return {
            type: 'point',
            x: this.x,
            y: this.y,
            radius: this.sizeInMeters,
            color: this.color
        };
    }

    fromJSON(json: any): void {
        if (json.type === 'point') {
            this.x = json.x;
            this.y = json.y;
            this.sizeInMeters = json.sizeInMeters;
            this.color = json.color;
        }
    }

    protected rotateImplementation(angle: number, centerX: number, centerY: number): void {
        const dx = this.x - centerX;
        const dy = this.y - centerY;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        this.x = centerX + dx * cos - dy * sin;
        this.y = centerY + dx * sin + dy * cos;
    }

    public moveTo(x: number, y: number): void {
        this.x = x;
        this.y = y;
    }

    getLineSegments(): { x1: number, y1: number, x2: number, y2: number }[] {
        return [];
    }

    public override scaleImplementation(scaleX: number, scaleY: number, anchorX: number, anchorY: number): void {
        //cannot scale point elements
    }

    override updateForm(): void {
        // Convert point from meters to kilometers for storage
        const pointInKm = [
            this.x / 1000, // Convert m to km
            this.y / 1000  // Convert m to km
        ];
        
        // Update the form control
        const control = this.formGroup.get('LocationPoints');
        if (control) {
            const currentValue = control.value;
            if (JSON.stringify(currentValue) !== JSON.stringify(pointInKm)) {
                control.setValue(pointInKm);
                control.markAsDirty();
                console.log('PointElement: Form marked as dirty due to point changes');
            }
        }

        // Update rotation in form if control exists (convert radians to degrees)
        const rotationControl = this.formGroup.get('DisplayAngle');
        if (rotationControl) {
            const angleDegrees = this.DisplayAngle;
            const currentRotation = rotationControl.value;
            if (currentRotation !== angleDegrees) {
                rotationControl.setValue(Math.round(angleDegrees));
                rotationControl.markAsDirty();
                console.log('PointElement: Form marked as dirty due to rotation changes');
            }
        }
    }

    protected override isInsidePolygon(polygonElement: LevelDesignerElementBase): boolean {
        return polygonElement.isPointInside(this.x, this.y);
    }

    public override getPropertyMetadata(): PropertyMetadata[] {
        return [
            {
                formControlName: 'DisplayAngle',
                displayName: 'Rotation',
                inputType: { type: 'angle' },
                description: 'Rotation angle of the element',
                order: 99,
                category: 'Transform'
            }
        ];
    }
}
